import React, { useState, useEffect } from 'react';
import Table, {
  columnType,
  tempFilteredRows,
} from '../../../components/ClearingTable/Table';
import { IconButton, Modal, Backdrop, Fade, Box } from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { listDetailsProfitAndLoss } from '../../../services/ProfitAndLossService';
import { formatCurrencySymbol } from '../../../lib/fmt2';
import {
  CustomBodyRenderAlign,
  CustomBodyRenderQty,
  CustomBodyRenderCurrencyLeft,
} from '../../../components/Table/CustomBodyRender';
import useStyles from '../../../styles';
import { useConfirm } from 'material-ui-confirm';

export default function ProfitAndLossTableDetailsTable({
  onClose: handleClose,
  open: isOpen,
  value,
}) {
  const classes = useStyles();
  const confirm = useConfirm();

  const [rows, setRows] = useState([]);
  const [searchDataCsv, setSearchDataCsv] = React.useState([]);
  const [pagination, setPagination] = useState({});
  const [totalRows, setTotalRows] = useState(0);
  const [previousRows, setPreviousRows] = useState(0);
  const [footerData, setFooterData] = useState({});

  const columns = [
    {
      name: 'processDate',
      label: 'Process Date',
      type: columnType.date,
    },
    {
      name: 'buyDate',
      label: 'Buy Date',
      type: columnType.date,
    },
    {
      name: 'sellDate',
      label: 'Sell Date',
      type: columnType.date,
    },
    {
      name: 'buyTrnsId',
      label: 'Buy Trns ID',
      options: {
        display: false,
      },
    },
    {
      name: 'sellTrnsId',
      label: 'Sell Trns ID',
    },
    {
      name: 'correspondent',
      label: 'Correspondent',
    },
    {
      name: 'branch',
      label: 'Branch',
    },
    {
      name: 'accountNo',
      label: 'Account No',
    },
    {
      name: 'masterAccountNo',
      label: 'Master Account No',
    },
    {
      name: 'rep',
      label: 'Rep',
    },
    {
      name: 'assetType',
      label: 'Asset Type',
    },
    {
      name: 'symbol',
      label: 'Symbol',
    },
    {
      name: 'symbolDescription',
      label: 'Symbol Description',
    },
    {
      name: 'plType',
      label: 'PL Type',
    },
    {
      name: 'entryType',
      label: 'Entry Type',
    },
    {
      name: 'closedQty',
      label: 'Closed Qty',
      type: columnType.quantity,
    },
    {
      name: 'buyPrice',
      label: 'Buy Price',
      type: columnType.price,
    },
    {
      name: 'sellPrice',
      label: 'Sell Price',
      type: columnType.price,
    },
    {
      name: 'buyNetAmt',
      label: 'Buy Amount Price',
      type: columnType.price,
    },
    {
      name: 'sellNetAmt',
      label: 'Sell Amount Price',
      type: columnType.price,
    },
    {
      name: 'realized',
      label: 'Realized',
      type: columnType.amount,
      addFooter: true,
    },
    {
      name: 'closingPrice',
      label: 'Closing Price',
      type: columnType.price,
    },
    {
      name: 'marketValue',
      label: 'Market Value',
      type: columnType.amount,
      addFooter: true,
    },
    {
      name: 'ytdRealized',
      label: 'YTD Realized',
      type: columnType.amount,
    },
    {
      name: 'subAccountNo',
      label: 'Sub Account No',
      options: {
        display: false,
      },
    },
    {
      name: 'shortTermGain',
      label: 'Short Term Gain',
      type: columnType.amount,
    },
    {
      name: 'shortTermLoss',
      label: 'Short Term Loss',
      type: columnType.amount,
    },
    {
      name: 'shortTermNet',
      label: 'Short Term Net',
      type: columnType.amount,
    },
    {
      name: 'longTermGain',
      label: 'Long Term Gain',
      type: columnType.amount,
    },
    {
      name: 'longTermLoss',
      label: 'Long Term Loss',
      type: columnType.amount,
    },
    {
      name: 'longTermNet',
      label: 'Long Term Net',
      type: columnType.amount,
    },
    {
      name: 'ytdShortTermGain',
      label: 'YTD Short Term Gain',
      type: columnType.amount,
    },
    {
      name: 'ytdShortTermLoss',
      label: 'YTD Short Term Loss',
      type: columnType.amount,
    },
    {
      name: 'ytdShortTermNet',
      label: 'YTD Short Term Net',
      type: columnType.amount,
    },
    {
      name: 'ytdLongTermGain',
      label: 'YTD Long Term Gain',
      type: columnType.amount,
    },
    {
      name: 'ytdLongTermLoss',
      label: 'YTD Long Term Loss',
      type: columnType.amount,
    },
    {
      name: 'ytdLongTermNet',
      label: 'YTD Long Term Net',
      type: columnType.amount,
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    selectableRows: 'none',
  };

  useEffect(
    () => {
      async function init() {
        handleSearch();
      }

      if (isOpen) {
        init();
      }
    }, // eslint-disable-next-line
    [isOpen]
  );

  const getCsvData = async () => {
    if (totalRows === 0) {
      return [];
    }
    const MAX_RECORDS_ALLOWED = 100000;

    let paginationCopy = {
      ...pagination,
      reload: false,
      pageNo: 0,
      rowsPerPage: MAX_RECORDS_ALLOWED,
    };

    const data = await listDetailsProfitAndLoss(searchDataCsv, paginationCopy);

    if (data.summary.totalRows >= MAX_RECORDS_ALLOWED) {
      confirm({
        description:
          'Only ' +
          MAX_RECORDS_ALLOWED.toLocaleString() +
          ' items will be downloaded, if you need more please use the metabase.',
        confirmationText: 'I understand',
      });
    }

    return data.profitandlossdetailsList;
  };

  const handleSearch = async (paginationChange) => {
    try {
      let paginationCopy = {
        ...pagination,
        reload: false,
        pageNo: paginationChange === true ? pagination.pageNo : 0,
        rowsPerPage: pagination.rowsPerPage || 100,
      };

      if (previousRows != 0) {
        if (previousRows < pagination.rowsPerPage * pagination.pageNo) {
          paginationCopy.pageNo = 0;
        }
      }

      const data = await listDetailsProfitAndLoss(value, paginationCopy);

      setSearchDataCsv(value);
      setTotalRows(data.summary.totalRows);
      setPreviousRows(totalRows);
      paginationCopy.count = data.summary.totalRows;
      setPagination(paginationCopy);
      setRows(data.profitandlossdetailsList);

      setFooterData({
        realized: formatCurrencySymbol(data.summary.realized, '', 2),
        marketValue: formatCurrencySymbol(data.summary.marketValue, '', 2),
      });
    } catch (error) {
      console.log(error.message);
    }
  };

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      className={classes.modalBackdrop}
      open={isOpen}
      onClose={() => {
        handleClose();
      }}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
    >
      <Fade in={isOpen}>
        <div className={classes.tableModalFull}>
          <Box component="div" mt={2}>
            <Table
              title="PROFIT AND LOSS DETAILS"
              data={rows}
              columns={columns}
              options={options}
              pagination={pagination}
              setPagination={setPagination}
              onSearch={handleSearch}
              getCsvData={getCsvData}
              footerData={footerData}
            />
          </Box>

          <div className={classes.modalFooter}>
            <div className={classes.grdCellNone} style={{ marginRight: 10 }}>
              <div className={classes.modalCloseIcon} style={{ marginRight: 10 }}>
                <IconButton
                  aria-label="close"
                  onClick={() => {
                    handleClose();
                  }}
                >
                  <CloseIcon style={{ color: '#f1f1f1' }} />
                </IconButton>
              </div>
            </div>
          </div>
        </div>
      </Fade>
    </Modal>
  );
}
